import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calculator, ArrowRight, Star, Check, ShieldCheck, Clock, Flame, Crown,
  Sparkles, Calendar, MessageCircle, Dumbbell, Mail, Loader2,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { trackLead } from '../hooks/useAnalytics';
import { LAURENT_IMAGE, BOOKING_URL } from '../data/constants';
import BeforeAfterGallery from '../components/BeforeAfterGallery';

const STEPS = [
  { icon: Calculator, fr: ['1 · Calculez', 'Vos besoins caloriques exacts en 2 min, gratuitement.'], en: ['1 · Calculate', 'Your exact calorie needs in 2 min, for free.'] },
  { icon: Flame, fr: ['2 · Recevez', 'Un plan & des conseils personnalisés à votre profil.'], en: ['2 · Get', 'A plan & tips tailored to your profile.'] },
  { icon: Crown, fr: ['3 · Transformez', 'Passez au coaching humain avec Laurent quand vous voulez.'], en: ['3 · Transform', 'Switch to human coaching with Laurent whenever you want.'] },
];

const BENEFITS = [
  ['Sans carte bancaire pour le résultat gratuit', 'No credit card for the free result'],
  ['Calcul intelligent (sexe, âge, cycle, objectif)', 'Smart calc (sex, age, cycle, goal)'],
  ['Plan alimentaire 7 jours avec vraies recettes', '7-day meal plan with real recipes'],
  ['Coaching humain en option avec un vrai coach', 'Optional human coaching with a real coach'],
];

const FunnelPage = () => {
  const { language } = useLanguage();
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const formRef = useRef(null);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!/.+@.+\..+/.test(email)) return;
    setBusy(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tracking/lead`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'funnel_demarrer' }),
      });
    } catch { /* lead best-effort */ }
    trackLead({ source: 'funnel_demarrer' });
    // bodyup_lead_email supprimé (C1-StorageHygiene - pas nécessaire)
    navigate('/calculateur-calories');
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    formRef.current?.querySelector('input')?.focus();
  };

  return (
    <>
      <SEOHead title={L('Calculez vos calories gratuitement | Body-Up', 'Calculate your calories for free | Body-Up')} description={L('Obtenez votre plan nutritionnel personnalisé gratuitement. Coaching en ligne avec Laurent Waelti, Body-Up Nyon.', 'Get your personalized nutrition plan for free. Online coaching with Laurent Waelti, Body-Up Nyon.')} path="/demarrer" />
      <main className="min-h-screen bg-black text-white">
        {/* Hero */}
        <section className="relative overflow-hidden pt-28 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
          <div className="relative max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full mb-5">
              <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />)}</div>
              <span className="text-xs font-semibold text-zinc-300">{L('4.9/5 · 28 avis Google · Nyon', '4.9/5 · 28 Google reviews · Nyon')}</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {L('Combien de calories', 'How many calories')}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-500 to-purple-400">{L('pour atteindre votre objectif ?', 'to reach your goal?')}</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="text-zinc-300 mt-5 max-w-xl mx-auto text-base sm:text-lg">
              {L('Obtenez gratuitement votre nombre de calories et vos macros personnalisés. Puis un plan complet ou un coaching humain avec Laurent.', 'Get your personalized calories and macros for free. Then a full plan or human coaching with Laurent.')}
            </motion.p>
            <motion.form ref={formRef} onSubmit={submitLead} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mt-8 max-w-md mx-auto" data-testid="funnel-lead-form">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={L('Votre email', 'Your email')}
                    className="w-full pl-9 pr-3 py-4 rounded-2xl bg-white/5 border border-white/15 text-white placeholder-zinc-500 focus:border-primary focus:outline-none" data-testid="funnel-lead-email" />
                </div>
                <button type="submit" disabled={busy} className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-extrabold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.03] transition-transform shadow-lg shadow-primary/30 disabled:opacity-60 whitespace-nowrap" data-testid="funnel-primary-cta">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Calculator className="w-5 h-5" />} {L('Recevoir mon calcul', 'Get my result')}
                </button>
              </div>
              <div className="mt-3">
                <Link to="/transformation" className="inline-flex items-center justify-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="funnel-secondary-cta">
                  <Crown className="w-4 h-4 text-purple-300" /> {L('ou découvrir le coaching avec Laurent', 'or discover coaching with Laurent')}
                </Link>
              </div>
            </motion.form>
            <p className="text-xs text-zinc-500 mt-4 inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-green-400" /> {L('Gratuit · Sans carte bancaire · Résultat immédiat', 'Free · No credit card · Instant result')}</p>
          </div>
        </section>

        {/* Steps */}
        <section className="px-4 sm:px-6 lg:px-8 pb-16">
          <div className="max-w-4xl mx-auto grid sm:grid-cols-3 gap-4">
            {STEPS.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="rounded-2xl bg-zinc-900/60 border border-white/10 p-5" data-testid={`funnel-step-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/25 to-purple-500/20 flex items-center justify-center mb-3"><s.icon className="w-5 h-5 text-primary" /></div>
                <h3 className="font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{(en ? s.en : s.fr)[0]}</h3>
                <p className="text-sm text-zinc-400 mt-1">{(en ? s.en : s.fr)[1]}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Real results — social proof aligned with /transformation */}
        <section className="px-4 sm:px-6 lg:px-8 pb-16">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-full border border-primary/30 mb-3">
                <Flame className="w-4 h-4 text-primary" /><span className="text-xs font-bold text-white tracking-wider">{L('RÉSULTATS RÉELS', 'REAL RESULTS')}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{L('Ils ont commencé exactement comme vous', 'They started exactly like you')}</h2>
              <p className="text-zinc-400 mt-2 text-sm sm:text-base max-w-xl mx-auto">{L('Des clients Body-Up accompagnés par Laurent — un calcul, un plan, et de la régularité.', 'Body-Up clients coached by Laurent — one calculation, one plan, and consistency.')}</p>
            </div>
            <BeforeAfterGallery language={en ? 'en' : 'fr'} limit={3} />
            <div className="text-center mt-8">
              <Link to="/transformation" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-bold text-white border border-purple-500/40 hover:bg-purple-500/10 transition-colors" data-testid="funnel-transformation-link">
                <Crown className="w-4 h-4 text-purple-300" /> {L('Découvrir les formules coaching', 'Discover coaching plans')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Benefits + Coach */}
        <section className="px-4 sm:px-6 lg:px-8 pb-16">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>{L('Pourquoi commencer ici ?', 'Why start here?')}</h2>
              <ul className="space-y-3">
                {BENEFITS.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-zinc-200">
                    <span className="w-6 h-6 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-4 h-4 text-emerald-400" /></span>
                    <span className="text-sm sm:text-base">{en ? b[1] : b[0]}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-3 text-xs text-zinc-400">
                <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-primary" /> {L('2 minutes', '2 minutes')}</span>
                <span className="inline-flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5 text-primary" /> {L('Messagerie 24/7 (pack coaching)', '24/7 messaging (coaching pack)')}</span>
                <span className="inline-flex items-center gap-1.5"><Dumbbell className="w-3.5 h-3.5 text-primary" /> {L('Programmes muscu perso', 'Custom training')}</span>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden border border-white/10 h-80">
              <img src={LAURENT_IMAGE} alt="Laurent Waelti" className="absolute inset-0 w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-white font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Laurent Waelti</p>
                <p className="text-zinc-300 text-sm mb-3">{L('Votre coach · Body-Up Nyon', 'Your coach · Body-Up Nyon')}</p>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-purple-500 to-primary hover:scale-[1.02] transition-transform" data-testid="funnel-booking-cta">
                  <Calendar className="w-4 h-4" /> {L('Réserver un appel découverte', 'Book a discovery call')}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-4 sm:px-6 lg:px-8 pb-24">
          <div className="max-w-3xl mx-auto rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 to-black p-8 sm:p-10 text-center">
            <Sparkles className="w-8 h-8 text-primary mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{L('Prêt à connaître vos chiffres ?', 'Ready to know your numbers?')}</h2>
            <p className="text-zinc-400 mt-2 mb-6">{L('Lancez votre calcul gratuit maintenant — aucun engagement.', 'Start your free calculation now — no commitment.')}</p>
            <button onClick={scrollToForm} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-extrabold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.03] transition-transform shadow-lg shadow-primary/30 text-lg" data-testid="funnel-final-cta">
              <Calculator className="w-5 h-5" /> {L('Calculer mes calories', 'Calculate my calories')} <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Sticky mobile CTA */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 p-3 bg-black/90 backdrop-blur border-t border-white/10" data-testid="funnel-sticky-cta">
          <button onClick={scrollToForm} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-extrabold text-white bg-gradient-to-r from-primary to-pink-500">
            <Calculator className="w-5 h-5" /> {L('Calculer gratuitement', 'Calculate for free')}
          </button>
        </div>
      </main>
    </>
  );
};

export default FunnelPage;
